import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import NotFound from './NotFound';
import { useProduct } from '@/hooks/useProduct';
import { ProductDetailLayout } from '@/components/product/ProductDetailLayout';
import { useProductColors } from '@/hooks/useProductColors';
import { useVariantImagePreloader } from '@/hooks/useVariantImagePreloader';
import { useProductVariants } from '@/hooks/useProductVariants';
import { useProductAttributes } from '@/hooks/useProductAttributes';
import { SEOHead } from '@/components/SEOHead';
import { 
  generateProductSchema, 
  generateBreadcrumbSchema, 
  generateFAQSchema,
  generateProductMetaTitle,
  generateProductMetaDescription 
} from '@/lib/seo';
import { trackViewContent } from '@/utils/analytics';
import { trackFunnelEvent } from '@/utils/funnelTracker';

const ProductDetailSkeleton = () => (
  <div className="container mx-auto px-4 py-8">
    <div className="h-6 w-64 bg-muted/50 rounded animate-pulse mb-6" />
    <div className="grid md:grid-cols-2 gap-8">
      <div className="space-y-4">
        <div className="aspect-square bg-muted/50 rounded-lg animate-pulse" />
        <div className="flex gap-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="w-20 h-20 bg-muted/50 rounded animate-pulse" />
          ))}
        </div>
      </div>
      <div className="space-y-6">
        <div className="h-8 bg-muted/50 rounded animate-pulse w-3/4" />
        <div className="h-6 bg-muted/50 rounded animate-pulse w-1/2" />
        <div className="h-24 bg-muted/50 rounded animate-pulse" />
        <div className="h-12 bg-muted/50 rounded-full animate-pulse" />
      </div>
    </div>
  </div>
);

const ProductDetail = () => {
  const { id: productId } = useParams<{ id: string }>();

  const { product, isLoading, error, relatedProducts, isLoadingRelated } = useProduct(productId);
  
  // Prefetch attributes at top level so they're cached for all ProductInfo instances
  const { isLoading: attributesLoading } = useProductAttributes(productId || '');
  
  // Preload variant images in background (don't block page render)
  useVariantImagePreloader(productId);
  
  // Load variants at top level to avoid double-loading
  const {
    variants,
    selectedVariant: variantFromHook,
    setSelectedVariant,
    isLoading: variantsLoading,
  } = useProductVariants(productId || '');

  // Memoize the selected variant to prevent unnecessary re-renders
  const selectedVariant = useMemo(() => {
    if (variantFromHook) {
      return variantFromHook;
    }
    // Fallback to product's default variant while variants are loading
    if (product?.defaultVariant && !variantFromHook) {
      return {
        id: product.defaultVariant.variantId,
        price: product.defaultVariant.price,
        discount_price: product.defaultVariant.discountPrice,
        is_discounted: product.defaultVariant.isDiscounted,
        stock: product.defaultVariant.stock,
        image_url: product.defaultVariant.imageUrl,
        pot_type: product.defaultVariant.potType,
        size: product.defaultVariant.size,
        pot_color: product.defaultVariant.potColor,
      };
    }
    return null;
  }, [variantFromHook, product?.defaultVariant]);
  
  const {
    availableColors,
    availableSizes,
    selectedColor,
    handleColorChange,
    handleCarouselNavigation
  } = useProductColors(product);

  const handleVariantChange = (variant: any) => {
    setSelectedVariant(variant);
  };

  // Track funnel product_view as soon as product loads
  const hasTrackedFunnel = useRef(false);
  useEffect(() => {
    if (product && productId && !hasTrackedFunnel.current) {
      hasTrackedFunnel.current = true;
      trackFunnelEvent('product_view', `/product/${productId}`, product.id);
    }
  }, [product, productId]);

  // Track ViewContent event for Facebook Pixel (needs variant for price)
  const hasTrackedView = useRef(false);
  useEffect(() => {
    if (product && selectedVariant && !hasTrackedView.current) {
      hasTrackedView.current = true;
      const price = selectedVariant?.discount_price || selectedVariant?.price || product.discount_price || product.price;
      trackViewContent({
        content_name: product.name,
        content_ids: [product.id],
        content_type: 'product',
        value: price,
        currency: 'INR',
      });
    }
  }, [product, selectedVariant]);

  // Early validation - if no productId, show 404
  if (!productId) {
    return <NotFound />;
  }

  // Wait for product data, variants, AND attributes before showing content
  const isReady = !isLoading && product && !variantsLoading && !attributesLoading;

  if (!isReady) {
    return <ProductDetailSkeleton />;
  }
  
  if (error) {
    console.error('Product detail error:', error);
    return <div className="text-center py-12 text-destructive">Error: {error.message}</div>;
  }

  if (!product) {
    return <NotFound />;
  }

  // Generate SEO data
  const productImages = product.image_urls || [];
  const displayPrice = selectedVariant?.price || product.price;
  const discountPrice = selectedVariant?.discount_price || product.discount_price;
  const categoryName = product.category?.[0] || undefined;
  
  const productSchema = generateProductSchema({
    name: product.name,
    description: product.description || undefined,
    image: productImages.length > 0 ? productImages : ['/placeholder.svg'],
    price: displayPrice,
    discountPrice: discountPrice || undefined,
    availability: (selectedVariant?.stock || product.stock) > 0 ? 'InStock' : 'OutOfStock',
    category: categoryName,
  });

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Shop', url: '/shop' },
    ...(categoryName ? [{ name: categoryName, url: `/shop?category=${categoryName}` }] : []),
    { name: product.name, url: `/product/${productId}` }
  ]);

  const schemas = [productSchema, breadcrumbSchema];

  // Add FAQ schema if product has FAQs
  const productFaqs = product.faq as { question: string; answer: string }[] | undefined;
  if (productFaqs && productFaqs.length > 0) {
    schemas.push(generateFAQSchema(productFaqs));
  }

  const metaTitle = product.meta_title || generateProductMetaTitle(product.name, categoryName);
  const metaDescription = product.meta_description || generateProductMetaDescription(
    product.name, 
    displayPrice, 
    product.description || undefined
  );

  return (
    <>
      <SEOHead
        title={metaTitle}
        description={metaDescription}
        canonical={`/product/${product.url_slug || productId}`}
        ogImage={productImages[0]}
        ogType="product"
        jsonLd={schemas}
      />
      <ProductDetailLayout
        product={product}
        selectedColor={selectedColor}
        selectedVariant={selectedVariant}
        availableSizes={availableSizes}
        availableColors={availableColors}
        onColorChange={handleColorChange}
        onVariantChange={handleVariantChange}
        onCarouselNavigation={handleCarouselNavigation}
        relatedProducts={relatedProducts}
        isLoadingRelated={isLoadingRelated}
        variants={variants}
      />
    </>
  );
};

export default ProductDetail;
